import {
  LocalStorageKeys,
  PRICE_CUSTOM_ATTRIBUTE_TITLE,
  PreferenceKey,
} from "@is/constants";
import { STYLE_LEVEL_ATTRIBUTES } from "@is/constants/common";
import { LocalStorageService } from "@is/services";
import {
  Attribute,
  AttributeSetting,
  CustomAttribute,
  PreferenceOption,
  SearchTerm,
  SearchTermType,
  Style,
  Translation,
} from "@is/types";

import { entriesFromObject } from "./object";

export const convertTextToAttributes = (
  text: string,
  preferences: PreferenceOption[]
): SearchTerm[] => {
  return preferences.reduce(
    (previous: SearchTerm[], current: PreferenceOption) => {
      current.options.forEach((preferenceOption: PreferenceOption) => {
        if (preferenceOption.label.toLowerCase() === text.toLocaleLowerCase()) {
          previous.push({
            key: preferenceOption.parentKey,
            value: preferenceOption.key,
            type: preferenceOption.type,
            label:
              preferenceOption.type === SearchTermType.Size ||
              preferenceOption.type === SearchTermType.Length
                ? `${preferenceOption.type}: ${preferenceOption.label}`
                : preferenceOption.label,
          });
        }

        preferenceOption.options?.forEach((subOption: PreferenceOption) => {
          if (subOption.label.toLowerCase() === text.toLocaleLowerCase()) {
            previous.push({
              key: subOption.parentKey,
              value: subOption.key,
              type: subOption.type,
              label:
                subOption.type === SearchTermType.Size ||
                subOption.type === SearchTermType.Length
                  ? `${subOption.type}: ${subOption.label}`
                  : subOption.label,
            });
          }
        });
      });
      return previous;
    },
    []
  );
};

export const getAppliedPreferenceOptions = (
  searchTerms: SearchTerm[],
  preferences: PreferenceOption[]
) => {
  const searchTermsOrder = {};
  const finalPreferences = preferences.reduce(
    (acc: PreferenceOption[], current: PreferenceOption) => {
      // we should ignore custom attributes as they are not part of the preferences
      if (current.custom) {
        return acc;
      }

      searchTerms.forEach((searchTerm: SearchTerm, searchTermIndex) => {
        // looking for search terms in categories
        current.options.forEach((preferenceOption: PreferenceOption) => {
          if (
            preferenceOption.key === searchTerm.value &&
            preferenceOption.type === searchTerm.type &&
            preferenceOption.parentKey === searchTerm.key
          ) {
            if (acc.some((item) => item.key === current.key)) {
              const index = acc.findIndex((item) => item.key === current.key);

              if (!acc[index].options) {
                acc[index].options = [];
              }

              acc[index].options.push({ key: preferenceOption.key });

              searchTermsOrder[current.key] = searchTermIndex;
            } else {
              acc.push({
                key: current.key,
                options: [{ key: preferenceOption.key }],
              });
              searchTermsOrder[current.key] = searchTermIndex;
            }
          }

          // looking for search terms in sub-categories
          preferenceOption.options?.forEach((subOption: PreferenceOption) => {
            if (
              subOption.key === searchTerm.value &&
              subOption.type === searchTerm.type &&
              subOption.parentKey === searchTerm.key &&
              subOption.categoryKey === searchTerm.categoryKey
            ) {
              if (acc.some((item) => item.key === current.key)) {
                const index = acc.findIndex((item) => item.key === current.key);
                const parentOption = acc[index].options?.find(
                  (item) => item.key === preferenceOption.key
                );
                if (parentOption) {
                  if (!parentOption.options) {
                    parentOption.options = [];
                  }
                  parentOption.options.push({ key: subOption.key });
                  searchTermsOrder[current.key] = searchTermIndex;
                } else {
                  if (!acc[index].options) {
                    acc[index].options = [];
                  }
                  acc[index].options.push({
                    key: preferenceOption.key,
                    options: [{ key: subOption.key }],
                  });
                  searchTermsOrder[current.key] = searchTermIndex;
                }
              } else {
                acc.push({
                  key: current.key,
                  options: [
                    {
                      key: preferenceOption.key,
                      options: [{ key: subOption.key }],
                    },
                  ],
                });
                searchTermsOrder[current.key] = searchTermIndex;
              }
            }
          });
        });
      });

      return acc;
    },
    []
  );

  return finalPreferences.sort(
    (prev, next) => searchTermsOrder[prev.key] - searchTermsOrder[next.key]
  );
};

export const getPrimaryAttributesWithSelectedSearchTerms = (
  primaryAttributes: AttributeSetting[],
  searchTerms: SearchTerm[]
) => {
  const preferencesWithSearchTerms: AttributeSetting[] = primaryAttributes.map(
    (attribute) => {
      const { key, label, type, visible, primary, singleSelection } = attribute;

      return {
        key,
        label,
        type,
        visible,
        primary,
        custom: false,
        singleSelection,
        options: searchTerms
          .filter((term) => term.key === key)
          .map((term) => ({ ...term, key: term.value })),
      };
    }
  );

  return preferencesWithSearchTerms;
};

export const getSortedSearchTermsByLastRemove = (
  searchTerms: SearchTerm[],
  preferences: PreferenceOption[],
  searchTermForRemoval: SearchTerm
) => {
  let parentKeyToMove = "";

  const searchTermsWithParentKeys = searchTerms.map((searchTerm) => {
    let parentKey = "";
    preferences.forEach((preference) => {
      if (
        preference.options.some(
          (option) =>
            option.key === searchTerm.value && option.type === searchTerm.type
        )
      ) {
        parentKey = preference.key;
      }

      preference.options.forEach((categoryOption) => {
        if (
          categoryOption.key === searchTermForRemoval.value &&
          categoryOption.type === searchTermForRemoval.type &&
          categoryOption.categoryKey === searchTermForRemoval.categoryKey
        ) {
          parentKeyToMove = preference.key;
        }

        categoryOption.options?.forEach((subcategoryOption) => {
          if (
            subcategoryOption.key === searchTermForRemoval.value &&
            subcategoryOption.type === searchTermForRemoval.type &&
            subcategoryOption.categoryKey === searchTermForRemoval.categoryKey
          ) {
            parentKeyToMove = preference.key;
          }
        });
      });
    });

    return {
      ...searchTerm,
      parentKey,
    };
  });

  const searchTermsWithoutRemoved = searchTermsWithParentKeys.filter(
    (searchTerm) =>
      !(
        searchTerm.value === searchTermForRemoval.value &&
        searchTerm.categoryKey === searchTermForRemoval.categoryKey
      ) &&
      !(
        searchTerm.categoryKey === searchTermForRemoval.value &&
        !searchTermForRemoval.categoryKey
      )
  );

  const otherSearchTermsWithSameCategory = searchTermsWithoutRemoved.filter(
    (searchTerm) =>
      searchTerm.parentKey === parentKeyToMove &&
      searchTerm.value !== searchTermForRemoval.value
  );

  const searchTermsMovedToEnd = [];
  otherSearchTermsWithSameCategory.forEach((categorySearchTerm) => {
    searchTermsMovedToEnd.push(categorySearchTerm);
    if (categorySearchTerm.type !== SearchTermType.Subcategory) {
      const subcategoriesSearchTerms = searchTermsWithoutRemoved.filter(
        (subcategorySearchTerm) =>
          subcategorySearchTerm.categoryKey === categorySearchTerm.value
      );
      searchTermsMovedToEnd.push(...subcategoriesSearchTerms);
    }
  });

  const filteredSearchTerms = searchTermsWithoutRemoved.filter(
    (searchTerm) =>
      !searchTermsMovedToEnd.some(
        (movedSearchTerm) =>
          searchTerm.key === movedSearchTerm.key &&
          searchTerm.value === movedSearchTerm.value &&
          searchTerm.type === movedSearchTerm.type &&
          searchTerm.parentKey === movedSearchTerm.parentKey
      )
  );

  filteredSearchTerms.push(...searchTermsMovedToEnd);

  return filteredSearchTerms.map((searchTerm) => {
    const editedSearchTerm = { ...searchTerm };
    delete editedSearchTerm.parentKey;

    return editedSearchTerm;
  });
};

export const getTranslatedValue = (translations: Translation[]) => {
  const locale = LocalStorageService.getItem(LocalStorageKeys.LanguageCode);

  return translations.find((translation) => translation.code === locale)
    ?.translation;
};

// The "Price attribute" is a custom attribute the operator adds in the Instore
// admin titled "Price" (case-insensitive) and WITHOUT options. Unlike other
// custom attributes — whose options become selectable filter values — this one
// drives the price range slider. Both signals are required: the title guards
// against an accidentally option-less attribute, and the empty options confirm
// it is the slider-style attribute rather than a normal value list.
export const isPriceCustomAttribute = (
  customAttribute: CustomAttribute
): boolean =>
  customAttribute?.title?.trim().toLowerCase() ===
    PRICE_CUSTOM_ATTRIBUTE_TITLE && customAttribute.options?.length === 0;

export const convertCustomAttributesToPreferences = (
  customAttributes: CustomAttribute[]
) =>
  customAttributes?.map(
    (customAttribute) =>
      ({
        custom: true,
        key: customAttribute.id,
        label:
          getTranslatedValue(customAttribute.translations) ??
          customAttribute.title,
        // The Price attribute is typed as Price so it renders as the range
        // slider in its configured position within the combined filter list;
        // all other custom attributes remain plain text value lists.
        type: isPriceCustomAttribute(customAttribute)
          ? SearchTermType.Price
          : SearchTermType.Text,
        options: customAttribute.options.map((customOption) => ({
          key: customOption.title,
          label:
            getTranslatedValue(customOption.translations) ?? customOption.title,
          parentKey: customAttribute.id,
          type: SearchTermType.Text,
        })),
      } as PreferenceOption)
  ) ?? [];

export const sortPreferencesByAttributeOrder = (
  preferences: PreferenceOption[],
  attributes: AttributeSetting[]
): PreferenceOption[] => {
  const keyOrder = attributes.map((attribute) => attribute.key);

  return preferences.sort((a, b) => {
    const indexA = keyOrder.indexOf(a.key);
    const indexB = keyOrder.indexOf(b.key);
    return indexA - indexB;
  });
};

export const unselectSingleSelectedAttributes = (
  parentKey: string,
  type: string,
  singleSelectionAttributes: AttributeSetting[],
  searchTerms: SearchTerm[],
  preferences: PreferenceOption[]
) => {
  let searchTagForUnselect = null;
  const isSingleSelection = singleSelectionAttributes.some(
    (attr) => attr.key === parentKey && attr.type === type
  );

  if (isSingleSelection) {
    searchTagForUnselect = searchTerms.find(
      (searchTerm) => searchTerm.key === parentKey && searchTerm.type === type
    );
  }

  if (searchTagForUnselect) {
    const filteredSearchTerms = getSortedSearchTermsByLastRemove(
      searchTerms,
      preferences,
      searchTagForUnselect
    );

    return filteredSearchTerms;
  }

  return searchTerms;
};

export const convertToSizeSearchTerm = (size: string): SearchTerm => {
  return {
    key: PreferenceKey.Sizes,
    value: size,
    type: SearchTermType.Size,
    label: size,
    isPrimary: true,
  };
};

export const convertToLengthSearchTerm = (length: string): SearchTerm => {
  return {
    key: PreferenceKey.Length,
    value: length,
    type: SearchTermType.Length,
    label: length,
    isPrimary: true,
  };
};

export const getSizesAsFilters = (size?: string, length?: string) => {
  const filters = [] as SearchTerm[];

  if (size) {
    filters.push(convertToSizeSearchTerm(size));
  }

  if (length) {
    filters.push(convertToLengthSearchTerm(length));
  }

  return filters;
};

export const getAttributesWithStyleLevelAttributes = (
  style: Style
): Attribute[] => {
  const attributes = [...(style.attributes ?? [])];

  entriesFromObject(STYLE_LEVEL_ATTRIBUTES).forEach(([key, label]) => {
    const value = style[key];
    const hasStyleLevelAttribute = attributes.some((attr) => attr.key === key);

    if (!hasStyleLevelAttribute && typeof value === "string" && value) {
      attributes.push({
        key,
        value,
        label,
      });
    }
  });

  return attributes;
};
